import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import Drawer from '../../components/Drawer';
import { Briefcase, Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';

function StatusPill({ status }) {
  const cls = status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200';
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${cls}`}>{status || 'active'}</span>;
}



/* ── New Employee Drawer ── */
function NewEmployeeDrawer({ open, onClose, token, onSuccess, currencySymbol }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', role: '', phone: '', email: '', join_date: new Date().toISOString().slice(0, 10), salary_amount: '', active_status: 'active', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm({ name: '', role: '', phone: '', email: '', join_date: new Date().toISOString().slice(0, 10), salary_amount: '', active_status: 'active', notes: '' });
  }, [open]);

  const submit = async () => {
    if (!form.name.trim()) { toast.show('Name is required', 'error'); return; }
    if (!form.role.trim()) { toast.show('Role is required', 'error'); return; }
    if (!form.salary_amount || parseFloat(form.salary_amount) < 0) { toast.show('Valid salary is required', 'error'); return; }
    setSaving(true);
    try {
      await api.employeeCreate(token, { ...form, salary_amount: parseFloat(form.salary_amount), phone: form.phone || undefined, email: form.email || undefined, notes: form.notes || undefined });
      toast.show('Employee created', 'success');
      onSuccess();
    } catch (e) { toast.show(e?.message || 'Create failed', 'error'); } finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} title="New Employee">
      <div className="space-y-4">
        <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Name *</span>
          <input data-testid="ne-emp-name" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.name} onChange={e => set('name', e.target.value)} /></label>
        <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Role *</span>
          <input data-testid="ne-emp-role" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.role} onChange={e => set('role', e.target.value)} placeholder="e.g. Teacher, Accountant" /></label>
        <div className="grid grid-cols-2 gap-4">
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Phone</span>
            <input data-testid="ne-emp-phone" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.phone} onChange={e => set('phone', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Email</span>
            <input data-testid="ne-emp-email" type="email" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.email} onChange={e => set('email', e.target.value)} /></label>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Join Date</span>
            <input data-testid="ne-emp-join" type="date" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.join_date} onChange={e => set('join_date', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Monthly Salary ({currencySymbol}) *</span>
            <input data-testid="ne-emp-salary" type="number" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.salary_amount} onChange={e => set('salary_amount', e.target.value)} placeholder="0" /></label>
        </div>
        <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Notes</span>
          <textarea data-testid="ne-emp-notes" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200 h-20 resize-none" value={form.notes} onChange={e => set('notes', e.target.value)} /></label>
        <button onClick={submit} disabled={saving} data-testid="ne-emp-submit"
          className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm disabled:opacity-50 hover:shadow-lg transition-all">
          {saving ? 'Creating...' : 'Create Employee'}
        </button>
      </div>
    </Drawer>
  );
}

/* ── Main Employees Page ── */
export default function Employees() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { formatMoney, currencySymbol } = useTenantFormat();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 25;
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await api.employeesList(token, { q: search || undefined, active_status: statusFilter || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
      setItems(r.items || []);
      setTotal(r.total ?? (r.items || []).length);
    } catch (e) {
      if (e?.status === 403) return;
      setItems([]);
    } finally { setLoading(false); }
  }, [token, search, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="employees-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 flex items-center gap-3"><Briefcase size={28} className="text-indigo-600" /> Employees</h1>
          <p className="text-gray-500 text-sm mt-1">{total} employee{total !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setNewOpen(true)} data-testid="new-employee-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-bold shadow-lg hover:shadow-xl hover:brightness-110 transition-all">
          <Plus size={16} /> New Employee
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="employee-search" placeholder="Search by name..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none" />
        </div>
        <select data-testid="employee-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white text-gray-700 font-semibold">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="employees-empty">
          <div className="w-20 h-20 rounded-2xl bg-indigo-50 flex items-center justify-center mb-5"><Briefcase size={36} className="text-indigo-400" /></div>
          <h3 className="text-gray-900 font-bold text-lg mb-2">No employees yet</h3>
          <p className="text-gray-500 text-sm mb-5">Add your first employee to start tracking payroll.</p>
          <button onClick={() => setNewOpen(true)} className="px-5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"><Plus size={14} className="inline mr-1.5" />Add Employee</button>
        </div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden" data-testid="employees-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Name</th>
                <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Role</th>
                <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Phone</th>
                <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Join Date</th>
                <th className="text-right px-4 py-3 text-gray-500 text-xs font-bold uppercase">Salary</th>
                <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Status</th>
              </tr></thead>
              <tbody>
                {items.map(emp => (
                  <tr key={emp.id} data-testid={`employee-row-${emp.id}`} onClick={() => navigate(`/school/employees/${emp.id}`)}
                    className="border-b border-gray-100 last:border-0 hover:bg-indigo-50/40 cursor-pointer transition-colors h-[52px]">
                    <td className="px-5 py-3 text-gray-900 font-semibold">{emp.name}</td>
                    <td className="px-4 py-3 text-gray-600">{emp.role || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 tabular-nums">{emp.phone || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{emp.join_date ? new Date(emp.join_date).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="px-4 py-3 text-gray-900 text-right tabular-nums font-semibold">{formatMoney(emp.salary_amount)}</td>
                    <td className="px-4 py-3"><StatusPill status={emp.active_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-gray-500 text-xs">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50"><ChevronLeft size={14} className="inline" /> Prev</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">Next <ChevronRight size={14} className="inline" /></button>
            </div>
          </div>
        </>
      )}

      <NewEmployeeDrawer open={newOpen} onClose={() => setNewOpen(false)} token={token} onSuccess={() => { setNewOpen(false); load(); }} currencySymbol={currencySymbol} />
    </div>
  );
}
